import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategory } from './CategoryContext';
import { LocalCategoryModel } from '../models';
import { MenuItem } from '../models';
import { LeafMark, SprigDivider } from './Ornaments';
import '../styles.css';

interface CategoriesPageProps {
  categories: LocalCategoryModel[];
  selectedLanguage: 'en' | 'gr'; // Add language prop
  setLogoVisible: (visible: boolean) => void; // Add prop for setting logo visibility
}

const translations = {
  en: {
    backToRoot: "← Back to Main Page",
  },
  gr: {
    backToRoot: "← Πίσω στην Αρχική",
  },
};

const CategoriesPage: React.FC<CategoriesPageProps> = ({ categories, selectedLanguage, setLogoVisible }) => {
  const navigate = useNavigate();
  const { setSelectedCategory } = useCategory();

  useEffect(() => {
    setLogoVisible(false);
    return () => setLogoVisible(true);
  }, [setLogoVisible]);

  const hasAvailableItems = (category: LocalCategoryModel): boolean => {
    // Check if there are any available items in the category's direct items
    const hasDirectItems = category.items?.some((item: MenuItem) => item.available) || false;

    // Check if there are any available items in subcategories
    const hasSubcategoryItems = category.subcategories?.some((subcategory) =>
      subcategory.items?.some((item: MenuItem) => item.available)
    ) || false;

    return hasDirectItems || hasSubcategoryItems;
  };

  const getRandomItemImage = (category: LocalCategoryModel): string | null => {
    const itemsWithImages = category.items?.filter((item: MenuItem) => item.img) || [];
    if (itemsWithImages.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * itemsWithImages.length);
    return itemsWithImages[randomIndex].img || null;
  };

  const handleCategoryClick = (category: LocalCategoryModel) => {
    setSelectedCategory(category);
    navigate(`/items/${category.categoryID}`);
  };

  return (
    <>
      <div className="back-arrow-container">
        <button
          className="back-arrow-button"
          onClick={() => navigate('/', { replace: true })}
        >
          {translations[selectedLanguage].backToRoot}
        </button>
      </div>

      <div className="categories-page">
        <header className="menu-section-head">
          <span className="menu-eyebrow">
            <LeafMark className="menu-eyebrow__mark" />
            {selectedLanguage === 'en' ? 'Browse' : 'ΠΕΡΙΗΓΗΣΗ'}
          </span>
          <h2 className="categories-page__title">
            {selectedLanguage === 'en' ? 'Categories' : 'Κατηγορίες'}
          </h2>
          <SprigDivider className="menu-divider" />
        </header>

        <div className="category-list">
          {categories
            .filter((category) => hasAvailableItems(category))
            .map((category) => {
              const randomItemImage = getRandomItemImage(category);

              return (
                <div
                  key={category.categoryID}
                  className="category-card"
                  onClick={() => handleCategoryClick(category)}
                >
                  <div className="category-details">
                    <h3 className="category-title">{category.categoryName}</h3>
                  </div>

                  {randomItemImage && (
                    <div className="category-image-container">
                      <img
                        src={randomItemImage}
                        alt={category.categoryName}
                        className="category-image"
                        onError={(e) => {
                          console.error(`Failed to load image: ${randomItemImage}`);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};

export default CategoriesPage;
